import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, User, Building2, FolderOpen, FileText, AlertCircle, Search, ChevronDown } from 'lucide-react';
import api from '../utils/api';

const QuickAddTaskModal = ({ 
  isOpen, 
  onClose, 
  onTaskCreated, 
  onOpenFullEditor, 
  user,
  prefilledData = {} 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    project_id: '',
    description: '',
    due_date: '',
    assigned_to: user?.id || '',
    ...prefilledData
  });

  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [tenantOrganization, setTenantOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Search states
  const [clientSearch, setClientSearch] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      // Reset form with prefilled data when modal opens
      setFormData({
        title: '',
        client_id: '',
        project_id: '',
        description: '',
        due_date: '',
        assigned_to: user?.id || '',
        ...prefilledData
      });
      setErrors({});
      // Reset search states
      setClientSearch('');
      setProjectSearch('');
      setShowClientDropdown(false);
      setShowProjectDropdown(false);
    }
  }, [isOpen]); // Remove user?.id and prefilledData from dependencies

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowClientDropdown(false);
        setShowProjectDropdown(false);
      }
    };

    if (showClientDropdown || showProjectDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showClientDropdown, showProjectDropdown]);

  useEffect(() => {
    if (formData.client_id) {
      if (formData.client_id === 'tenant-organization') {
        loadCompanyProjects();
      } else {
        loadClientProjects(formData.client_id);
      }
    } else {
      setProjects([]);
      // Don't update formData here as it causes infinite loop
    }
  }, [formData.client_id]); // Removed function dependencies to fix temporal dead zone

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientsRes, teamRes, customValuesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/team/members'),
        api.get('/custom-values')
      ]);

      setClients(clientsRes.data || []);
      const rawMembers = teamRes.data?.members || teamRes.data || [];
      const normalizedMembers = rawMembers.map((member) => ({
        id: member.user?.id || member.user_id || member.id,
        name: member.user?.name || member.name || member.email || 'Unknown User'
      })).filter(m => !!m.id);
      setTeamMembers(normalizedMembers);
      
      // Find the tenant organization name from custom values
      const customValues = customValuesRes.data || [];
      const companyNameValue = customValues.find(v => v.variable_name === 'company_name');
      if (companyNameValue && companyNameValue.value) {
        setTenantOrganization({
          id: 'tenant-organization',
          name: companyNameValue.value,
          isTenant: true
        });
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClientProjects = useCallback(async (clientId) => {
    try {
      const response = await api.get(`/clients/${clientId}/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error loading client projects:', error);
      setProjects([]);
    }
  }, []);

  const loadCompanyProjects = useCallback(async () => {
    try {
      // Pass 'null' as client_id to filter for organization projects only (projects with no client)
      const response = await api.get('/projects?client_id=null');
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error loading company projects:', error);
      setProjects([]);
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Task title is required';
    }
    
    if (!formData.client_id) {
      newErrors.client_id = 'Client is required';
    }
    
    if (!formData.project_id) {
      newErrors.project_id = 'Project is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Clear project_id when client changes
      if (field === 'client_id' && value !== prev.client_id) {
        newData.project_id = '';
        // Immediately clear projects array to prevent showing wrong projects
        setProjects([]);
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const handleSaveTask = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const cleanAssignedTo = formData.assigned_to && formData.assigned_to !== 'undefined'
        ? formData.assigned_to
        : null;

      const taskData = {
        name: formData.title,
        description: formData.description,
        status: 'To Do',
        priority: 'Normal',
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        assignees: cleanAssignedTo ? [cleanAssignedTo] : []
      };

      // Always create tasks via the project endpoint
      // This works for both client projects and company projects
      const response = await api.post(`/projects/${formData.project_id}/tasks`, taskData);

      onTaskCreated(response.data);
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      console.error('QuickAdd create error response:', error.response?.data);
      setErrors({
        submit: error.response?.data?.detail || error.message || 'Failed to create task. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFullEditor = () => {
    if (!validateForm()) return;
    
    // Pass the current form data to the full editor
    onOpenFullEditor(formData);
    onClose();
  };

  const getClientName = (clientId) => {
    if (clientId === 'tenant-organization' && tenantOrganization) {
      return `${tenantOrganization.name} (Organization)`;
    }
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : '';
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : '';
  };

  const getUserName = (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    return member ? member.name : '';
  };

  // Filter functions for searchable dropdowns
  const getFilteredClients = () => {
    const allOptions = [];
    
    // Add tenant organization first if available
    if (tenantOrganization) {
      allOptions.push({
        id: tenantOrganization.id,
        name: `${tenantOrganization.name} (Organization)`,
        isOrganization: true
      });
    }
    
    // Add clients
    clients.forEach(client => {
      allOptions.push({
        id: client.id,
        name: client.name,
        isOrganization: false
      });
    });

    if (!clientSearch) return allOptions;
    
    return allOptions.filter(option =>
      option.name.toLowerCase().includes(clientSearch.toLowerCase())
    );
  };

  const getFilteredProjects = () => {
    if (!projectSearch) return projects;
    
    return projects.filter(project =>
      project.name.toLowerCase().includes(projectSearch.toLowerCase())
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-xl font-semibold text-white">Quick Add Task</h2>
                <p className="text-purple-100 text-sm">Create a new task quickly or open full editor</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-purple-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Client Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Client *
                </label>
                <div className="relative">
                  <div className={`w-full px-4 py-3 border rounded-lg focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent ${
                    errors.client_id ? 'border-red-300' : 'border-gray-300'
                  } cursor-pointer`}
                    onClick={() => setShowClientDropdown(!showClientDropdown)}
                  >
                    <div className="flex items-center justify-between">
                      <span className={formData.client_id ? 'text-gray-900' : 'text-gray-500'}>
                        {formData.client_id ? getClientName(formData.client_id) : 'Select a client'}
                      </span>
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  
                  {showClientDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                      <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <input
                            type="text"
                            value={clientSearch}
                            onChange={(e) => setClientSearch(e.target.value)}
                            placeholder="Search clients..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredClients().map((option) => (
                          <div
                            key={option.id}
                            onClick={() => {
                              handleInputChange('client_id', option.id);
                              setShowClientDropdown(false);
                              setClientSearch('');
                            }}
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                              formData.client_id === option.id ? 'bg-purple-50 text-purple-700' : 'text-gray-900'
                            }`}
                          >
                            {option.name}
                          </div>
                        ))}
                        {getFilteredClients().length === 0 && (
                          <div className="px-4 py-2 text-gray-500 text-center">
                            No clients found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {errors.client_id && (
                  <p className="text-red-600 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.client_id}
                  </p>
                )}
              </div>

              {/* Project Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FolderOpen className="w-4 h-4 inline mr-1" />
                  Project *
                </label>
                <div className="relative">
                  <div className={`w-full px-4 py-3 border rounded-lg focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent ${
                    errors.project_id ? 'border-red-300' : 'border-gray-300'
                  } ${!formData.client_id ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => {
                      if (formData.client_id) {
                        setShowProjectDropdown(!showProjectDropdown);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className={formData.project_id ? 'text-gray-900' : 'text-gray-500'}>
                        {!formData.client_id 
                          ? 'Select a client first' 
                          : formData.project_id 
                            ? getProjectName(formData.project_id) 
                            : 'Select a project'
                        }
                      </span>
                      {formData.client_id && <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>
                  
                  {showProjectDropdown && formData.client_id && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                      <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <input
                            type="text"
                            value={projectSearch}
                            onChange={(e) => setProjectSearch(e.target.value)}
                            placeholder="Search projects..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredProjects().map((project) => (
                          <div
                            key={project.id}
                            onClick={() => {
                              handleInputChange('project_id', project.id);
                              setShowProjectDropdown(false);
                              setProjectSearch('');
                            }}
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                              formData.project_id === project.id ? 'bg-purple-50 text-purple-700' : 'text-gray-900'
                            }`}
                          >
                            {project.name}
                          </div>
                        ))}
                        {getFilteredProjects().length === 0 && (
                          <div className="px-4 py-2 text-gray-500 text-center">
                            No projects found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {errors.project_id && (
                  <p className="text-red-600 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.project_id}
                  </p>
                )}
              </div>

              {/* Task Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter task title..."
                />
                {errors.title && (
                  <p className="text-red-600 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.title}
                  </p>
                )}
              </div>

              {/* Due Date and Assigned User (Same Line) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => handleInputChange('due_date', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    Assigned User
                  </label>
                  <select
                    value={formData.assigned_to}
                    onChange={(e) => handleInputChange('assigned_to', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Unassigned</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                  placeholder="Enter task description..."
                />
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    {errors.submit}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSaveTask}
                disabled={saving || loading}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <span>{saving ? 'Saving...' : 'Save Task'}</span>
              </button>
              
              <button
                onClick={handleOpenFullEditor}
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <span>Open Task Editor</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickAddTaskModal;